import { getApiBaseUrl } from "@/core/environment";
import { clearAuthToken, getAuthToken, persistUserData, saveAuthToken } from "./auth.service";
import type { AuthCallbackData, AuthLogoutResponse, AuthRefreshResponse } from "./auth.types";

const AUTH_PATHS = {
  LOGIN: "/auth/login",
  REFRESH: "/auth/refresh",
  LOGOUT: "/auth/logout",
} as const;

function getAuthUrl(path: string): string {
  return new URL(path, `${getApiBaseUrl().replace(/\/+$/, "")}/`).toString();
}

function getAuthorizationHeaders(): HeadersInit {
  const internalToken = getAuthToken();
  return internalToken ? { Authorization: `Bearer ${internalToken}` } : {};
}

export function getBffLoginUrl(): string {
  return getAuthUrl(AUTH_PATHS.LOGIN);
}

export function redirectToBffLogin(): void {
  window.location.assign(getBffLoginUrl());
}

/**
 * Persiste somente o token interno emitido pelo BFF.
 * Será chamado pelo handler de callback quando o formato do redirect for definido.
 */
export function persistBffSession(data: AuthCallbackData): void {
  saveAuthToken(data.internalToken);
  persistUserData({
    sub: data.user.id,
    name: data.user.name,
    email: data.user.email,
    roles: data.user.roles,
    scopes: [],
  });
}

export async function refreshAuthSession(): Promise<string> {
  const response = await fetch(getAuthUrl(AUTH_PATHS.REFRESH), {
    method: "POST",
    headers: {
      Accept: "application/json",
      ...getAuthorizationHeaders(),
    },
    credentials: "include",
  });

  const payload = (await response.json()) as AuthRefreshResponse;
  if (!response.ok || !payload.success || !payload.data?.token) {
    throw new Error(payload.message || "Erro ao renovar token");
  }

  saveAuthToken(payload.data.token);
  return payload.data.token;
}

export async function logoutFromBff(): Promise<void> {
  try {
    const response = await fetch(getAuthUrl(AUTH_PATHS.LOGOUT), {
      method: "POST",
      headers: {
        Accept: "application/json",
        ...getAuthorizationHeaders(),
      },
      credentials: "include",
    });

    const payload = (await response.json()) as AuthLogoutResponse;
    if (!response.ok || !payload.success) {
      throw new Error(payload.message || "Erro ao realizar logout");
    }
  } finally {
    clearAuthToken();
  }
}
